import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Smile, Meh, Frown } from "lucide-react";
import { toast } from "sonner";

const SENTIMENT_CONFIG = {
  positive: { label: "Positif", color: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: Smile, rowBg: "bg-emerald-50/50 border-emerald-200" },
  neutral:  { label: "Neutre",  color: "bg-slate-100 text-slate-700 border-slate-300",      Icon: Meh,   rowBg: "bg-card" },
  negative: { label: "Négatif", color: "bg-red-100 text-red-800 border-red-300",           Icon: Frown, rowBg: "bg-red-50/50 border-red-200" },
};

const SentimentBadge = ({ sentiment }) => {
  const cfg = SENTIMENT_CONFIG[sentiment] || SENTIMENT_CONFIG.neutral;
  const { Icon } = cfg;
  return (
    <Badge variant="outline" className={`${cfg.color} text-xs gap-1`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </Badge>
  );
};

export const LifeEventsTab = ({ employee, lifeEvents, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ event_date: "", event_time: "", title: "", comment: "", sentiment: "neutral" });
  const [filter, setFilter] = useState("all");

  const openCreate = () => {
    setEditing(null);
    setForm({
      event_date: new Date().toISOString().slice(0, 10),
      event_time: "", title: "", comment: "", sentiment: "neutral",
    });
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      event_date: ev.event_date || "",
      event_time: ev.event_time || "",
      title: ev.title || "",
      comment: ev.comment || "",
      sentiment: ev.sentiment || "neutral",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.event_date || !form.comment.trim()) {
      toast.error("Date et commentaire requis");
      return;
    }
    try {
      const payload = {
        event_date: form.event_date,
        event_time: form.event_time || null,
        title: form.title,
        comment: form.comment,
        sentiment: form.sentiment || "neutral",
      };
      if (editing) {
        await api.updateEmployeeLifeEvent(editing.id, payload);
        toast.success("Événement mis à jour");
      } else {
        await api.createEmployeeLifeEvent(employee.id, payload);
        toast.success("Événement ajouté");
      }
      setDialogOpen(false);
      setEditing(null);
      onRefresh();
    } catch {
      toast.error("Erreur de sauvegarde");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try {
      await api.deleteEmployeeLifeEvent(id);
      toast.success("Événement supprimé");
      onRefresh();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const visible = filter === "all" ? lifeEvents : lifeEvents.filter((e) => (e.sentiment || "neutral") === filter);
  const counts = {
    positive: lifeEvents.filter((e) => e.sentiment === "positive").length,
    neutral:  lifeEvents.filter((e) => !e.sentiment || e.sentiment === "neutral").length,
    negative: lifeEvents.filter((e) => e.sentiment === "negative").length,
  };

  return (
    <div className="space-y-4">
      <Card data-testid="life-events-card">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <CardTitle className="font-heading">Événements marquants</CardTitle>
            <div className="flex gap-1 ml-2">
              {[
                { k: "all", label: `Tous (${lifeEvents.length})`, color: "bg-slate-100" },
                { k: "positive", label: `😊 ${counts.positive}`, color: "bg-emerald-100 text-emerald-800" },
                { k: "neutral",  label: `😐 ${counts.neutral}`,  color: "bg-slate-100 text-slate-700" },
                { k: "negative", label: `😕 ${counts.negative}`, color: "bg-red-100 text-red-800" },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setFilter(t.k)}
                  className={`text-xs px-2 py-1 rounded-full border transition-opacity ${t.color} ${filter === t.k ? "" : "opacity-50"}`}
                  data-testid={`life-event-filter-${t.k}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={openCreate} data-testid="add-life-event-btn">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          {visible.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-6 bg-muted/30 rounded">
              Aucun événement {filter !== "all" ? `"${SENTIMENT_CONFIG[filter]?.label || ""}"` : "marquant"} enregistré.
            </p>
          ) : (
            <div className="space-y-2">
              {visible.map((ev) => {
                const s = ev.sentiment || "neutral";
                const cfg = SENTIMENT_CONFIG[s] || SENTIMENT_CONFIG.neutral;
                return (
                  <div
                    key={ev.id}
                    className={`flex items-start justify-between p-3 rounded border ${cfg.rowBg}`}
                    data-testid={`life-event-${ev.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="font-mono font-medium">
                          {new Date(ev.event_date).toLocaleDateString("fr-FR")}
                          {ev.event_time ? ` à ${ev.event_time}` : ""}
                        </span>
                        <SentimentBadge sentiment={s} />
                        {ev.title && <Badge variant="outline">{ev.title}</Badge>}
                      </div>
                      <p className="text-sm mt-1 whitespace-pre-wrap">{ev.comment}</p>
                      {ev.created_by && (
                        <p className="text-xs text-muted-foreground mt-1">Ajouté par {ev.created_by}</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(ev)} data-testid={`edit-life-event-${ev.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(ev.id)} data-testid={`delete-life-event-${ev.id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'événement" : "Nouvel événement marquant"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date" value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  data-testid="life-event-date"
                />
              </div>
              <div>
                <Label>Heure</Label>
                <Input
                  type="time" value={form.event_time}
                  onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                  data-testid="life-event-time"
                />
              </div>
            </div>
            <div>
              <Label>Tonalité</Label>
              <div className="flex gap-2 mt-1">
                {["positive", "neutral", "negative"].map((s) => {
                  const cfg = SENTIMENT_CONFIG[s];
                  const { Icon } = cfg;
                  const active = form.sentiment === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm({ ...form, sentiment: s })}
                      className={`flex-1 flex items-center justify-center gap-1 text-sm px-3 py-2 rounded border transition-all ${
                        active ? `${cfg.color} border-current ring-2 ring-offset-1 ring-current/30 font-semibold` : "bg-muted/30 hover:bg-muted text-muted-foreground"
                      }`}
                      data-testid={`life-event-sentiment-${s}`}
                    >
                      <Icon className="h-4 w-4" /> {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Titre (optionnel)</Label>
              <Input
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex : Prime exceptionnelle, Incident chantier..."
                data-testid="life-event-title"
              />
            </div>
            <div>
              <Label>Commentaire *</Label>
              <Textarea
                value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
                rows={5} data-testid="life-event-comment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSave} data-testid="save-life-event-btn">
              {editing ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LifeEventsTab;
